import { useEffect, useState, useRef } from 'react';
import { Col, Container, Row } from 'reactstrap';
import Layout from '../../templates/Layout';
import { useJobId, useSelectedAsset } from '../../../lib/util';
import {
  GET_EKG_FIELDS,
  GET_JOB_DETAILS,
  GET_JOBS_BY_EVENT,
  GET_MO_DETAILS,
  UPDATE_JOB,
} from '../../../graph-queries/tar/job';
import { useMutation, useQuery } from '@apollo/client';
import { TJob, TSapMo } from '../../../lib/types';
import classes from './JobDetails.module.scss';
import { Button, Nav, Spinner, Alert, Close, Offcanvas } from '@bphxd/ds-core-react';
import {
  Alert24,
  Edit16,
  HomeTerraced24,
  RotateCheck24,
  Settings24,
  ShoppingCart24,
  TailLeft16,
  TailRight16,
  BulletList16,
  Map16,
} from '@bphxd/ds-core-react/lib/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import JobTasksTable from '../../organisms/JobTasksTable/JobTasksTable';
import JobWorkpacks from '../../organisms/JobAspects/JobWorkpacks';
import JobScaffolds from '../../organisms/JobAspects/JobScaffolds';
import JobGoc from '../../organisms/JobAspects/JobGoc';
import JobEmocs from '../../organisms/JobAspects/JobEmocs';
import { FormModal } from '../../molecules/FormModal/FormModal';
import JobForm from '../../organisms/JobForm/JobForm';
import JobIsolations from '../../organisms/JobAspects/JobIsolations';
import { GET_CONFIG } from '../../../graph-queries/common/config';
import JobInsulations from '../../organisms/JobAspects/JobInsulations';
import JobLeakTestPack from '../../organisms/JobAspects/JobLeakTestPacks';
import JobProcedure from '../../organisms/JobAspects/JobProcedures';
import JobLiftPlan from '../../organisms/JobAspects/JobLiftPlans';
import JobPurchaseOrder from '../../organisms/JobAspects/JobPurchaseOrders';
import { useHasUserAccess } from '../../../lib/util/auth';
import { pick } from 'lodash';
import { TARS_AND_OUTAGES_PATH } from '../../../constants';

const renderRequirementFromBoolean = (value?: boolean | null) => {
  return value === true ? 'Required' : value === false ? 'Not Required' : '';
};

const renderYesNoFromBoolean = (value?: boolean | null) => {
  return value === true ? 'Yes' : value === false ? 'No' : '';
};
export const useAizeSearchUrlByTagNumber = (tagNumber: string) => {
  const asset = useSelectedAsset();
  const tagNumberWithoutLocationPrefix = tagNumber.slice(tagNumber.indexOf('-') + 1);
  return (asset?.aizeLink || '#') + tagNumberWithoutLocationPrefix;
};

const JobDetailsPage = () => {
  const hasUserAccess = useHasUserAccess();
  const navigate = useNavigate();
  const { data: config } = useQuery(GET_CONFIG);
  const { search, hash, state } = useLocation();
  const selectedAsset = useSelectedAsset();
  const jobId = useJobId();
  const { data, loading, error: jobDetailsError } = useQuery(GET_JOB_DETAILS, { variables: { jobId } });
  const [editNotes, setEditNotes] = useState<boolean>(false);
  const [alertText, setAlertText] = useState('');
  const [offCanvasToggle, setOffCanvasToggle] = useState<boolean>(false);

  const jobIds: number[] = state?.jobIds || [];
  const jobIndex = jobIds.indexOf(jobId);
  const prevJobId = jobIds[jobIndex - 1];
  const nextJobId = jobIds[jobIndex + 1];

  const isRealTimeEnabled = !!config?.config.featureFlags.find(
    (ff) => ff.flag === 'FEATURE_FLAG_REALTIME_MO_API'
  )?.isEnabled;

  const { data: moData, loading: moLoading, error: moDetailsError } = useQuery(GET_MO_DETAILS, {
    variables: { moNumber: data?.job?.moNumber || '', isRealTime: isRealTimeEnabled },
    skip: !data?.job?.moNumber,
  });
  const {
    data: ekgData,
    loading: ekgLoading,
    error: ekgError,
  } = useQuery(GET_EKG_FIELDS, {
    variables: { location: moData?.sapMo.location || '' },
    skip: !moData?.sapMo.location,
  });
  const [updateJob] = useMutation(UPDATE_JOB, {
    refetchQueries: [
      {
        query: GET_JOB_DETAILS,
        variables: { jobId: jobId },
      },
      {
        query: GET_JOBS_BY_EVENT,
        variables: { eventId: data?.job.eventId || -1 },
      },
    ],
  });
  const fullAizeUrl = useAizeSearchUrlByTagNumber(ekgData?.ekgFields.tagNumber || '');
  const moDetails = moData?.sapMo as unknown as TSapMo;
  const jobDetails = data?.job as unknown as TJob;
  const mediaUrl = `${process.env.REACT_APP_FRONTEND_URL}/materials?asset=${selectedAsset?.assetName}&floc=${moDetails?.location}`;
  const [notes, setNotes] = useState(jobDetails?.notes || '');
  useEffect(() => {
    if (alertText) {
      const timer = setTimeout(() => setAlertText(''), 8000);
      return () => clearTimeout(timer);
    }
  }, [alertText]);
  useEffect(() => {
    if (jobDetails) {
      setNotes(jobDetails.notes);
    }
  }, [jobDetails]);

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  // Adjust the notes textarea height based on the content
  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = 'auto'; // Reset height to auto to shrink if needed
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`; // Set height based on scrollHeight
    }
  }, [notes]); // Trigger on every text update

  const handleNotesClick = async () => {
    const input = pick(jobDetails, [
      'eventId',
      'fieldWalk',
      'functionalLocation',
      'workpackRequired',
      'mocRequired',
      'materialsRequired',
      'longLeadMaterialsRequired',
      'specialistVendorsRequired',
      'specialistToolingRequired',
      'scaffoldRequired',
      'insulationRequired',
      'liftPlanRequired',
      'ropeAccessRequired',
      'isolationRequired',
      'riggingRequired',
      'heatTracingRequired',
      'jobDescription',
      'jobId',
      'moNumber',
      'scheduleStatus',
      'notes',
    ]);
    setEditNotes(!editNotes); // Toggle the value
    if (editNotes) {
      //If user clicks submit then update db
      input.notes = notes; //UPDATE TO NOTES COLUMN, USING GOC FIELD FOR LOCAL TESTING
      updateJob({ variables: { input } }).then(() =>
        setAlertText('Job notes successfully updated')
      );
    }
  };

  const renderJobForm = (onClose: () => void, job: TJob) => {
    return (
      <JobForm
        defaultValues={job}
        onSubmit={async (job: TJob) => {
          const { updatedBy, updatedAt, aspectStatuses, ...input } = job;
          return updateJob({ variables: { input } }).then(() =>
            setAlertText('Job successfully updated')
          );
        }}
        onClose={onClose}
        type={'edit'}
      />
    );
  };

  const refreshPage = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    window.location.reload();
  };

  useEffect(() => {
    if (jobDetails && selectedAsset && jobDetails?.tarEvent?.assetId !== selectedAsset?.assetId) {
      navigate(`/${TARS_AND_OUTAGES_PATH}/events?asset=${selectedAsset?.assetName}`, { replace: true });
    }
  }, [jobDetails, selectedAsset]);

  const replacePath = (path: string) => () => navigate(path, { state, replace: true });

  const error = jobDetailsError || moDetailsError || ekgError;

  return (
    <Layout>
      <Container>
        {loading || moLoading ? (
          'Loading...'
        ) : (
          <div className={classes.root}>
            <section>
              <Button
                level="quartenary"
                Icon={BulletList16}
                onClick={() => {
                  navigate(-1);
                }}
              >
                Back to event
              </Button>
              {prevJobId && (
                <Button
                  level="quartenary"
                  Icon={TailLeft16}
                  onClick={() => navigate(`/${TARS_AND_OUTAGES_PATH}/jobs/${prevJobId}${search}`, { state, replace: true })}
                >
                  Previous job
                </Button>
              )}
              {nextJobId && (
                <Button
                  level="quartenary"
                  Icon={TailRight16}
                  iconPosition="end"
                  onClick={() => navigate(`/${TARS_AND_OUTAGES_PATH}/jobs/${nextJobId}${search}`, { state, replace: true })}
                >
                  Next job
                </Button>
              )}
              <>
                <Button
                  level="quartenary"
                  Icon={Map16}
                  iconPosition="end"
                  onClick={() => {
                    setOffCanvasToggle(!offCanvasToggle);
                  }}
                >
                  Show Job IDs
                </Button>
                <Offcanvas
                  style={{ top: '121px', height: 'calc(100% - 121px)' }}
                  isOpen={offCanvasToggle}
                  direction="start"
                  size="md"
                  toggle={() => offCanvasToggle}
                  scrollable={true}
                  backdrop={false}
                >
                  <Offcanvas.Header
                    size="md"
                    toggle={() => {
                      setOffCanvasToggle(!offCanvasToggle);
                    }}
                  >
                    Job Numbers
                  </Offcanvas.Header>
                  <Offcanvas.Body>
                    <ul style={{ listStyleType: 'none', padding: 0 }}>
                      {jobIds.map((jobId) => (
                        <li key={jobId} style={{ padding: '8px 0' }}>
                          <button
                            style={{
                              all: 'unset', // Resets default button styles
                              color: jobId === jobDetails.jobId ? 'black' : 'lightgray',
                              fontWeight: jobId === jobDetails.jobId ? 'bold' : 'normal',
                              fontSize: '14px',
                              cursor: 'pointer',
                            }}
                            onClick={() =>
                              navigate(`/${TARS_AND_OUTAGES_PATH}/jobs/${jobId}${search}`, { state, replace: true })
                            }
                          >
                            {jobId}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </Offcanvas.Body>
                </Offcanvas>
              </>
            </section>
            <section>
              {error && (
                <Alert
                  className={classes.alert}
                  color="warning"
                  dismissible
                  fade
                  iconStrokeWidth={2}
                >
                  Some data on this page didn&apos;t load completely.{' '}
                  <a href="#" onClick={refreshPage}>
                    Refresh the page
                  </a>{' '}
                  to try loading the data again.
                </Alert>
              )}
              {alertText && (
                <Alert
                  className={classes.alert}
                  color="success"
                  dismissible
                  fade
                  iconStrokeWidth={2}
                >
                  {alertText}
                </Alert>
              )}
            </section>
            <section className="border border-primary bg-white">
              <header className="p-4">
                <div className={classes.title}>
                  Job #{jobDetails?.jobId} {jobDetails?.jobDescription}
                </div>
                <FormModal
                  title="Job Settings"
                  renderForm={(onClose) => renderJobForm(onClose, jobDetails)}
                  renderButton={(onClick) => (
                    <Button
                      disabled={!hasUserAccess}
                      rounded="0"
                      size="sm"
                      level="tertiary"
                      Icon={Edit16}
                      onClick={onClick}
                      title="Edit Job Settings"
                    >
                      Edit Job Details
                    </Button>
                  )}
                />
              </header>
              <div className="p-4">
                <Row className="g-12">
                  <Col>
                    <div className={classes.field}>
                      <div className={classes.label}>MO Number</div>
                      <div className={classes.value}>
                        <a href={moDetails?.viewInFiori || ''} target="_blank" rel="noreferrer">
                          {jobDetails?.moNumber}
                        </a>
                      </div>
                    </div>
                    <div className={classes.field}>
                      <div className={classes.label}>MO Status</div>
                      <div className={classes.value}>{moDetails?.statusFioriDescription}</div>
                    </div>
                    <div className={classes.field}>
                      <div className={classes.label}>Maintenance Plan Number</div>
                      <div className={classes.value}>{moDetails?.pm}</div>
                    </div>
                    <div className={classes.field}>
                      <div className={classes.label}>Safety Critical</div>
                      <div className={classes.value}>
                        {Boolean(moDetails?.safetyCritical).toString()}
                      </div>
                    </div>
                    <div className={classes.field}>
                      <div className={classes.label}>Functional Location</div>
                      <div className={classes.value}>
                        <a href={mediaUrl} target="_blank" rel="noreferrer">
                          {moDetails?.location}
                        </a>
                      </div>
                    </div>
                    <div className={classes.field}>
                      <div className={classes.label}>System Number</div>
                      <div className={classes.value}>
                        {ekgLoading && <Spinner size="sm" />}
                        {ekgLoading && <span className="visually-hidden">Loading...</span>}
                        {ekgError && <Alert24 title="error" />}
                        {ekgData?.ekgFields.systemNumber}
                      </div>
                    </div>
                    <div className={classes.field}>
                      <div className={classes.label}>Tag Number</div>
                      <div className={classes.value}>
                        <a href={fullAizeUrl} target="_blank" rel="noreferrer">
                          {ekgLoading && <Spinner size="sm" />}
                          {ekgLoading && <span className="visually-hidden">Loading...</span>}
                          {ekgError && <Alert24 title="error" />}
                          {ekgData?.ekgFields.tagNumber || ''}
                        </a>
                        {/* Removed as part of UI review. Not deleting it yet keeping in mind a possibility of re-adding. To be deleted later.
                          <span className={classes.view3d}>
                          <Preview24 />
                          View in 3D
                        </span> */}
                      </div>
                    </div>
                  </Col>
                  <Col>
                    <div className={classes.field}>
                      <div className={classes.label}>Nesting Code</div>
                      <div className={classes.value}>{moDetails?.nestingCode}</div>
                    </div>

                    <div className={classes.field}>
                      <div className={classes.label}>Type</div>
                      <div className={classes.value}>{moDetails?.workType}</div>
                    </div>
                    <div className={classes.field}>
                      <div className={classes.label}>Work Centre</div>
                      <div className={classes.value}>{moDetails?.mainWorkCenter}</div>
                    </div>
                    <div className={classes.field}>
                      <div className={classes.label}>Target Completion Date</div>
                      <div className={classes.value}>{moDetails?.basicFinishDate}</div>
                    </div>
                    <div className={classes.field}>
                      <div className={classes.label}>Revision Code</div>
                      <div className={classes.value}>{moDetails?.revisionCode}</div>
                    </div>
                  </Col>
                  <Col>
                    <div className={classes.field}>
                      <div className={classes.label}>Rope Access</div>
                      <div className={classes.value}>
                        {jobDetails?.ropeAccessRequired === true
                          ? 'Yes'
                          : jobDetails?.ropeAccessRequired === false
                            ? 'No'
                            : 'Not available'}
                      </div>
                    </div>
                    <div className={classes.field}>
                      <div className={classes.label}>Heat Tracing</div>
                      <div className={classes.value}>
                        {renderYesNoFromBoolean(jobDetails?.heatTracingRequired)}
                      </div>
                    </div>
                    <div className={classes.field}>
                      <div className={classes.label}>Specialist Vendors</div>
                      <div className={classes.value}>
                        {renderRequirementFromBoolean(jobDetails?.specialistVendorsRequired)}
                      </div>
                    </div>
                    <div className={classes.field}>
                      <div className={classes.label}>Specialist Tooling</div>
                      <div className={classes.value}>
                        {renderRequirementFromBoolean(jobDetails?.specialistToolingRequired)}
                      </div>
                    </div>
                    <div className={classes.field}>
                      <div className={classes.label}>Long-lead materials</div>
                      <div className={classes.value}>
                        {renderYesNoFromBoolean(jobDetails?.longLeadMaterialsRequired)}
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </section>
            <section className="border border-primary bg-white">
              <header className="p-4">
                <div className={classes.title}>Notes</div>
                <Button
                  disabled={!hasUserAccess}
                  rounded="0"
                  size="sm"
                  level="tertiary"
                  Icon={Edit16}
                  onClick={handleNotesClick}
                  title="Edit Job Notes"
                >
                  {!editNotes ? 'Edit Notes' : 'Submit'}
                </Button>
              </header>
              <textarea
                disabled={!editNotes}
                ref={textAreaRef}
                rows={3}
                name="notes"
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                style={{
                  width: '100%', // Keep the width the same
                  boxSizing: 'border-box', // Ensure padding and borders are included in the width
                  overflow: 'hidden', // Hide the scrollbar
                  resize: 'vertical', // Prevent manual resizing (handled automatically)
                }}
              />
            </section>
            <section className="border border-primary bg-white">
              <JobTasksTable tasks={moDetails?.labors} moNumber={moDetails?.workOrder} />
            </section>
            <section className={`border border-primary bg-white ${classes.aspects}`}>
              <Nav tabs>
                <Nav.Item>
                  <Nav.Link
                    active={!hash || hash === '#engineering'}
                    onClick={replacePath(`${search}#engineering`)}
                  >
                    <Settings24 /> Engineering
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    active={hash === '#support-services'}
                    onClick={replacePath(`${search}#support-services`)}
                  >
                    <HomeTerraced24 /> Support Services
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    active={hash === '#purchase-orders'}
                    onClick={replacePath(`${search}#purchase-orders`)}
                  >
                    <ShoppingCart24 />
                    Purchase Orders
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    active={hash === '#operations'}
                    onClick={replacePath(`${search}#operations`)}
                  >
                    <RotateCheck24 /> Operations
                  </Nav.Link>
                </Nav.Item>
              </Nav>

              {(!hash || hash === '#engineering') && (
                <>
                  <JobWorkpacks />
                  <JobGoc />
                  <JobEmocs />
                </>
              )}

              {hash === '#support-services' && (
                <>
                  <JobScaffolds />
                  <JobInsulations />
                  <JobLiftPlan />
                </>
              )}
              {hash === '#purchase-orders' && <JobPurchaseOrder />}
              {hash === '#operations' && (
                <>
                  <JobIsolations />
                  <JobLeakTestPack />
                  <JobProcedure />
                </>
              )}
            </section>
          </div>
        )}
      </Container>
    </Layout>
  );
};

JobDetailsPage.classes = classes;

export default JobDetailsPage;
